// Pure helpers for the configurable Work Plan sessions (Settings → Work plan → Session hours;
// GET/PUT /api/work-plan/sessions, and `sessions` in every work-plan bundle). The KEYS are fixed
// at `am` / `pm` — they are wire values on postings, MC requests and copy scopes — only the
// label and the hours are configurable. Framework-free so vitest covers the validation.

/** Fixed session keys, in day order. */
export const SESSION_KEYS = Object.freeze(["am", "pm"]);
/** Server cap on a session label (runes). */
export const SESSION_LABEL_MAX = 12;

/** The contract defaults the server seeds on first read (the clinics' real hours). */
export const DEFAULT_SESSION_DEFS = Object.freeze([
  Object.freeze({ key: "am", label: "AM", start: "08:30", end: "13:00" }),
  Object.freeze({ key: "pm", label: "PM", start: "14:00", end: "17:30" }),
]);

/** "HH:MM" → minutes since midnight, or -1 when malformed / past 23:59. */
export function minutesFromHHMM(value) {
  const match = /^(\d{2}):(\d{2})$/.exec(String(value || "").trim());
  if (!match) return -1;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return -1;
  return hours * 60 + minutes;
}

function clampRunes(value, max) {
  const runes = Array.from(String(value || "").trim());
  return runes.length > max ? runes.slice(0, max).join("") : runes.join("");
}

/**
 * Always two rows, am then pm: each taken from `raw` by key where present (label trimmed +
 * capped, times trimmed — NOT corrected, so an editor can show the error), else the contract
 * default for that key. Tolerates a non-array, extra keys and missing fields.
 */
export function normalizeSessionDefs(raw) {
  const list = Array.isArray(raw) ? raw : [];
  return SESSION_KEYS.map((key) => {
    const fallback = DEFAULT_SESSION_DEFS.find((def) => def.key === key);
    const found = list.find((item) => String(item?.key || "").trim().toLowerCase() === key);
    if (!found) return { ...fallback };
    return {
      key,
      label: clampRunes(found.label, SESSION_LABEL_MAX) || fallback.label,
      start: String(found.start ?? fallback.start).trim(),
      end: String(found.end ?? fallback.end).trim(),
    };
  });
}

/**
 * The server's rules, mirrored so the editor can refuse a bad save before the round-trip:
 * label 1–12 runes, valid "HH:MM" with start < end, and AM must end before PM starts.
 * → { ok, errors: { "am.label"?, "pm.label"?, "am.range"?, "pm.range"?, gap? } }
 */
export function validateSessionDefs(defs) {
  const rows = normalizeSessionDefs(defs);
  const errors = {};
  for (const row of rows) {
    const labelLength = Array.from(String(row.label || "").trim()).length;
    if (labelLength < 1 || labelLength > SESSION_LABEL_MAX) {
      errors[`${row.key}.label`] = `Label must be 1–${SESSION_LABEL_MAX} characters.`;
    }
    const start = minutesFromHHMM(row.start);
    const end = minutesFromHHMM(row.end);
    if (start < 0 || end < 0) {
      errors[`${row.key}.range`] = "Enter both times as HH:MM.";
    } else if (start >= end) {
      errors[`${row.key}.range`] = "The session must end after it starts.";
    }
  }
  const am = rows.find((row) => row.key === "am");
  const pm = rows.find((row) => row.key === "pm");
  if (!errors["am.range"] && !errors["pm.range"] && am && pm) {
    if (minutesFromHHMM(am.end) > minutesFromHHMM(pm.start)) {
      errors.gap = `${am.label || "AM"} must end before ${pm.label || "PM"} starts.`;
    }
  }
  return { ok: Object.keys(errors).length === 0, errors };
}

/** Label for a session key from the configured list; "AM"/"PM" style fallback when absent. */
export function sessionLabelFor(sessions, key) {
  const wanted = String(key || "").trim().toLowerCase();
  const found = (Array.isArray(sessions) ? sessions : []).find(
    (session) => String(session?.key || "").trim().toLowerCase() === wanted,
  );
  const label = String(found?.label || "").trim();
  return label || wanted.toUpperCase();
}

/** Field-by-field equality over the two rows — the editors' dirty check. */
export function sessionDefsEqual(a, b) {
  const left = normalizeSessionDefs(a);
  const right = normalizeSessionDefs(b);
  return left.every((row, index) => {
    const other = right[index];
    return row.key === other.key && row.label === other.label && row.start === other.start && row.end === other.end;
  });
}
